import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import { EGenderType } from "../../Enum/EGenderType"
import { EOldType } from "../../Enum/EOldType"
import { BaseModel } from "../Generics/BaseModel"
import { convertToLocalMoney } from "../Generics/Helps"

export class Promotions extends BaseModel {
  isCanUse: boolean = false

  id: number

  name: string

  description: string

  percentage: number

  dayOfWeek: number[]

  genderType: EGenderType

  startTime: string

  endTime: string

  fixedValue: number

  status: EEnableStatusType

  constructor(data: Partial<Promotions> = {}) {
    super()

    this.id = Number(data.id ?? -1)
    this.name = data.name ?? ""
    this.description = data.description ?? ""

    const percentage = data.percentage ?? null
    const fixedValue = data.fixedValue ?? null

    this.percentage = fixedValue !== null && fixedValue !== 0 ? 0 : percentage

    this.fixedValue = percentage !== null && percentage !== 0 ? 0 : fixedValue

    const day = data.dayOfWeek
    this.dayOfWeek = Array.isArray(day) ? day : this.safeParseDayOfWeek(day)

    this.genderType = data.genderType ?? EGenderType.All

    this.startTime = data.startTime || null
    this.endTime = data.endTime || null

    this.status = data.status ?? EEnableStatusType.Active
  }

  canApplyPromotion(date: Date, userGender: EGenderType, oldType: EOldType): boolean {
    if (this.status === EEnableStatusType.Inactive) {
      return false
    }

    const today = date.getDay()

    const isDayValid = this.dayOfWeek.length === 0 || this.dayOfWeek.includes(today)

    const now = this.getTimeNumber(date)

    const startValid = !this.startTime || now >= this.convertTimeToNumber(this.startTime)

    const endValid = !this.endTime || now <= this.convertTimeToNumber(this.endTime)

    const isGenderValid = (() => {
      switch (this.genderType) {
        case EGenderType.All:
          return true

        case EGenderType.Woman:
          return userGender === EGenderType.Woman

        case EGenderType.Man:
          return userGender === EGenderType.Man

        case EGenderType.Children:
          return oldType === EOldType.IsChildren

        case EGenderType.WomanAndMan:
          return userGender === EGenderType.Woman || userGender === EGenderType.Man

        case EGenderType.WomanAndChildren:
          return userGender === EGenderType.Woman || oldType === EOldType.IsChildren

        case EGenderType.ManAndChildren:
          return userGender === EGenderType.Man || oldType === EOldType.IsChildren

        default:
          return false
      }
    })()

    return isDayValid && isGenderValid && startValid && endValid
  }

  getDiscount(date: Date, userGender: EGenderType, oldType: EOldType, baseValue: number): number {
    if (!this.canApplyPromotion(date, userGender, oldType)) {
      return 0
    }

    if (this.percentage && this.percentage > 0) {
      return (baseValue * this.percentage) / 100
    }

    if (this.fixedValue && this.fixedValue > 0) {
      return this.fixedValue
    }

    return 0
  }

  getValueToStringMoney(): string {
    if (this.fixedValue && this.fixedValue > 0) {
      return convertToLocalMoney(this.fixedValue)
    }

    if (this.percentage && this.percentage > 0) {
      return `${this.percentage}%`
    }

    return ""
  }

  private safeParseDayOfWeek(value: any): number[] {
    if (!value) return []

    if (typeof value === "string") {
      return value
        .split(",")
        .map((e) => parseInt(e))
        .filter((e) => !isNaN(e))
    }

    return []
  }

  private convertTimeToNumber(time: string): number {
    const [hours, minutes] = time.split(":").map(Number)
    return hours * 60 + minutes
  }

  private getTimeNumber(date: Date): number {
    return date.getHours() * 60 + date.getMinutes()
  }
}
